import { debounce } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'src/redux/store';
import { AMOUNT_SOURCE_TYPE, CustomerGroup, setCustomerGroups } from '../../../../../../../../store/seriesAnswerSlice';

interface Props {
  customerCode: string;
  parentCustomerCode: string;
  methods: UseFormReturn<FieldValues, any>;
}

export const useDebounceHandleChange = (props: Props) => {
  const { customerCode, parentCustomerCode, methods } = props;
  const dispatch = useAppDispatch();
  const customerGroups = useAppSelector((state) => state.seriesAnswer.customerGroups);
  const debounceMap = useRef(new Map<string, any>());
  // useRefを使ってcustomerGroupsデータを保存し、再レンダリングを跨いで最新の値を保持する
  const customerGroupsRef = useRef<CustomerGroup[]>([]);

  // customerGroupsが変更された時、customerGroupsRefに最新データを設定
  useEffect(() => {
    customerGroupsRef.current = customerGroups;
  }, [customerGroups]);

  // コンポーネントがアンマウントされた時、debounce関数をクリアする
  useEffect(() => {
    const currentDebounceMap = debounceMap.current;
    return () => {
      // すべてのdebounce関数をキャンセル
      currentDebounceMap.forEach((fn) => fn.cancel());
      // debounceMapをクリア
      currentDebounceMap.clear();
    };
  }, []);

  // debounce処理の関数
  const debouncehandleChange = useMemo(
    () => (key: string) => {
      if (!debounceMap.current.has(key)) {
        // debounce関数がなければ、新しく作成してdebounceMapにセット
        debounceMap.current.set(
          key,
          debounce((value, name) => {
            // ほしいデータが配列で入っているので分割して抽出
            const tradeVolumeType = name.split('-')[0];
            const fiscalYear = tradeVolumeType === 'purchaseCurrentYear' || tradeVolumeType === 'salesCurrentYear' ? 'currentYear' : 'previousYear';
            const answerType = tradeVolumeType.match(/purchase/) ? 'answerPurchaseAmount' : 'answerSalesAmount';
            const isAccounts = tradeVolumeType === 'receivableBalance' || tradeVolumeType === 'payableBalance';
            const latestCustomerGroupsRef = customerGroupsRef.current;
            // 親会社コードが一致しているものを抽出し、該当する売上高等を更新する
            const customer = latestCustomerGroupsRef.find((customerGroup) => customerGroup.parentCustomerCode === parentCustomerCode);

            if (customer) {
              const updateChildren = customer.childrenCustomer.map((children) => {
                const isPurchase = tradeVolumeType.match(/purchase/);
                const currentChild = children?.currentYear;
                const preChild = children?.previousYear;

                const currentYearSourceType =
                  isPurchase !== null && fiscalYear === 'currentYear'
                    ? currentChild.systemPurchaseAmount !== value && currentChild.systemSalesAmount !== currentChild.answerSalesAmount
                      ? AMOUNT_SOURCE_TYPE.ANSWERED
                      : children.currentYear.sourceType
                    : isPurchase === null && fiscalYear === 'currentYear'
                    ? currentChild.systemPurchaseAmount !== currentChild.answerPurchaseAmount && currentChild.systemSalesAmount !== value
                      ? AMOUNT_SOURCE_TYPE.ANSWERED
                      : children.currentYear.sourceType
                    : children.currentYear.sourceType;

                const preYearSourceType =
                  isPurchase !== null && fiscalYear === 'previousYear'
                    ? preChild?.systemPurchaseAmount !== value && preChild.systemSalesAmount !== preChild.answerSalesAmount
                      ? AMOUNT_SOURCE_TYPE.ANSWERED
                      : children.previousYear.sourceType
                    : isPurchase === null && fiscalYear === 'previousYear'
                    ? preChild?.systemPurchaseAmount !== preChild.answerPurchaseAmount && preChild.systemSalesAmount !== value
                      ? AMOUNT_SOURCE_TYPE.ANSWERED
                      : children.previousYear.sourceType
                    : children.previousYear.sourceType;

                if (children.customerCode === customerCode) {
                  // comment,債権債務の場合かそれ以外
                  const element = !isAccounts
                    ? {
                        ...children,
                        [fiscalYear]: {
                          ...children[fiscalYear],
                          [answerType]: value,
                          sourceType: fiscalYear === 'currentYear' ? currentYearSourceType : preYearSourceType,
                        },
                      }
                    : {
                        ...children,
                        [tradeVolumeType]: value,
                      };
                  return element;
                }
                return children;
              });

              const newCustomerGroups: CustomerGroup[] = latestCustomerGroupsRef.map((customerGroup) => {
                if (customerGroup.parentCustomerCode === parentCustomerCode) {
                  return {
                    ...customerGroup,
                    childrenCustomer: updateChildren,
                  };
                }
                return customerGroup;
              });
              dispatch(setCustomerGroups([...newCustomerGroups]));
              methods.trigger([`salesComment-${customerCode}`, `purchaseComment-${customerCode}`]);
            }
            // 300msの遅延時間は同一の画面項目の入力に対してのみ適用されます。異なる画面項目（異なるkey）間での入力は相互に影響しません。
          }, 300),
        );
      }
      // debounce関数を返す
      return debounceMap.current.get(key);
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  /**
   * Autocompleteの候補が選ばれた時にグローバルステートを更新するための関数
   * @param name フィールド名 ex) salesComment-A1452950
   * @param value 事業撤退
   */
  const setCommentValue = (name: string, value: string) => {
    const key = name.split('-')[0];
    const customerCode = name.split('-')[1];

    const newCustomerGroups: CustomerGroup[] = customerGroups.map((customerGroup) => {
      if (customerGroup.parentCustomerCode === parentCustomerCode) {
        const updateChildren = customerGroup.childrenCustomer.map((children) => {
          if (children.customerCode === customerCode) {
            return {
              ...children,
              [key]: value,
            };
          }
          return children;
        });
        return { ...customerGroup, childrenCustomer: updateChildren };
      } else {
        return customerGroup;
      }
    });
    dispatch(setCustomerGroups(newCustomerGroups));
    methods.trigger([`salesComment-${customerCode}`, `purchaseComment-${customerCode}`]);
  };

  return { debouncehandleChange, setCommentValue };
};
